import Rpc from "@/Rpc";
import Events from "@/utils/Events";
import EventBus from "@/utils/EventBus";
import ActivityManager from "@/activity/ActivityManager";
import Log from "@/utils/Log"
import Perms, {setDynamicPermissions, setDynamicPermissionsForDashlets} from "@/server/Perms";
import FormConfig from "@/server/com/silkplm/engine/forms/schema/definition/FormConfig";
import DynamicFormConfig from "@/server/com/silkplm/engine/forms/schema/definition/DynamicFormConfig";
import PermissionsSet from "@/server/PermissionsSet";
import {getCustomizations} from "@/components/forms/customizations/FormsCustomizations";
import FormsListContext from "@/activity/templates/layouts/forms/FormsListContext";
import RpcClass from "@/server/RpcClass";
import DynamicEntity from "@/components/forms/DynamicEntity";
import ViewBase from "@/utils/ViewBase";
import SessionManager from "@/utils/SessionManager";
import CompanyConfigurationFormsLoader from "@/components/forms/CompanyConfigurationFormsLoader";
import CompanyConfigurationsFormsLoader from "@/components/forms/CompanyConfigurationFormsLoader";
import {
    findFieldValueFromCompanyCompanyConfigurationFormField,
} from "@/components/forms/customizations/plm/EntityEditFormService";
import {runtimeTr} from "@/utils/Tr";
import FormViewConfig from "@/server/com/silkplm/engine/forms/views/FormViewConfig";

const ActivityList = () => require("@/components/forms/ActivityList");
const ActivityEdit = () => require("@/components/forms/ActivityEdit");

class _Loader {
    constructor() {
        /** @type {module:src/server/com/silkplm/engine/forms/schema/definition/FormConfig.FormConfig[]} */
        this.forms = null;
        this.formsById = new Map();
        this.evaluators = new Map();
        /** @type {PermissionsSet} */
        this.permissions = null;

        this.customLists = new Map();
        this.customEditors = new Map();
        this.prototypes = new Map();

        this.eventListenerRegistered = false;
    }

    /**
     * @param {BaseFormConfig} formConfig
     * @return {string}
     */
    getFormTraceLabel(formConfig) {
        let code = "";
        if (formConfig.isDynamic) {
            code = "D-" + formConfig.formCode;
        } else {
            code = formConfig.formClass;
        }
        return code;
    }


    /**
     * @param {[{type:Type, activity:Activity}]}lists
     */
    setCustomLists(lists) {
        this.customLists = new Map();
        for (const list of lists) {
            let conf = FormsLoader.detectFormConfig(list.type, true);
            if (!conf) {
                continue;
            }
            this.customLists.set(conf.formId, list.activity);
        }
    }

    /**
     * @param {[{type:Type, activity:Activity}]}editors
     */
    setCustomEditors(editors) {
        this.customEditors = new Map();
        for (const editor of editors) {
            let conf = FormsLoader.detectFormConfig(editor.type, true);
            if (!conf) {
                continue;
            }
            this.customEditors.set(conf.formId, editor.activity);
        }
    }

    getCustomListEditor(objectToDeductForm) {
        let form = this.detectFormConfig(objectToDeductForm, true);
        if (!form) {
            return;
        }
        if (this.customLists.has(form.formId)) {
            return this.customLists.get(form.formId);
        }
        return null;
    }

    resetPermissions() {
        if (this.permissions == null) {
            this.permissions = new PermissionsSet();
            if (!this.eventListenerRegistered) {
                EventBus.$on(Events.permissionsChanged, () => this.resetPermissions());
                this.eventListenerRegistered = true;
            }
        }
        if (!this.forms) {
            return;
        }
        this.forms.forEach(form => {
            form.readPermitted = !form.restrictedRead || this.permissions[form.effectivePermissionRead];
            form.writePermitted = !form.restrictedWrite || this.permissions[form.effectivePermissionWrite];
            form.listPermitted = !form.restrictedList || this.permissions[form.effectivePermissionList];
        });
    }

    async _reloadAndSetup() {
        let dashlets = await Rpc.Dashboards.getAvailableDashlets();
        setDynamicPermissionsForDashlets(dashlets);

        this.forms = await Rpc.Forms.getForms();
        if (!CompanyConfigurationFormsLoader.getConfig()) {
            await CompanyConfigurationsFormsLoader.load();
        }
        let labelsDictionaries = await Rpc.Forms.getLabelDictionaries();
        this.formsById = new Map();
        this.forms.forEach(form => {
            Log.debug("Loading form: " + form.formTitle);
            this.formsById.set(form.formId, form);
            if (form.isDynamic) {
                this.prototypes.set(form.formClassLong, this.makePrototype(form));
            }
        });
        const entries = Object.entries(labelsDictionaries);


        this.forms.forEach(form => {
            let hasTranslations = false;
            form.translatedFields = [];
            form.originalEntityPlural = form.entityPlural;
            if (!form.isDynamic && AppTranslations.selectedLang !== 'en') {
                form.entityPlural = runtimeTr("api.forms." + form.formClass + ".plural", form.entityPlural);
                form.entityName = runtimeTr("api.forms." + form.formClass + ".singular", form.entityName);
                form.menuLabel = runtimeTr("api.forms." + form.formClass + ".menuLabel", form.entityName);
                form.formTitle = runtimeTr("api.forms." + form.formClass + ".title", form.formTitle);
            }
            for (let det of form.details) {
                if (det.tabTitle) {
                    det.tabTitle = runtimeTr("api.forms." + form.formClass + ".d." + det.name + ".title", det.tabTitle);
                }
            }
            for (let det of form.extensions) {
                if (det.tabTitle) {
                    det.tabTitle = runtimeTr("api.forms." + form.formClass + ".e." + det.name + ".title", det.tabTitle);
                }
            }
            for (let field of form.fields) {
                if (!form.isDynamic && AppTranslations.selectedLang !== 'en') {
                    if (form instanceof FormViewConfig) {
                        field.fieldLabel = runtimeTr("api.forms." + form.formClass + ".f." + field.fieldConfigId + ".label", field.fieldLabel);
                        field.gridColLabel = runtimeTr("api.forms." + form.formClass + ".f." + field.fieldConfigId + ".gridColLabel", field.gridColLabel);
                    } else {
                        field.fieldLabel = runtimeTr("api.forms." + form.formClass + ".f." + field.fieldName + ".label", field.fieldLabel);
                        field.gridColLabel = runtimeTr("api.forms." + form.formClass + ".f." + field.fieldName + ".gridColLabel", field.gridColLabel);
                    }
                }
                let lang = AppTranslations.selectedLang;
                if (lang !== 'en') {
                    field.shortHelpText = field.shortHelpTextTr[lang];
                    field.htmlHelpText = field.htmlHelpTextTr[lang];
                    field.helpWindowTitle = field.fieldLabel;
                    field.allowNullErrorMessage = $tr("fields.non-empty-info", "%s FIELD can't be empty");
                }

                let positionOfExpression = field.gridColLabel.indexOf("$");
                let expressionLabel = null;
                if (positionOfExpression !== -1) {
                    expressionLabel = field.gridColLabel.substring(positionOfExpression);
                }
                let customUserFieldLabel = findFieldValueFromCompanyCompanyConfigurationFormField(form,
                    field, "alternativeFieldLabel");
                // zmieniony label w konfiguracji organizacji
                if (customUserFieldLabel?.trim().length) {
                    if (expressionLabel?.length) {
                        field.gridColLabel = customUserFieldLabel + " " + expressionLabel;
                        field.fieldLabel = customUserFieldLabel + " " + expressionLabel;
                    } else {
                        field.gridColLabel = customUserFieldLabel;
                        field.fieldLabel = customUserFieldLabel;
                    }
                }
                entries.forEach(function ([key, value]) {
                    field.gridColLabel = field.gridColLabel.replace(key, value);
                    field.fieldLabel = field.fieldLabel.replace(key, value);
                });

                field.translations = findFieldValueFromCompanyCompanyConfigurationFormField(form, field, "translations");
                form.translatedFields.push(field.fieldName);
            }

            if (form.details) {
                form.details.forEach(detail => {
                    detail.formConfig = this.detectFormConfig(detail.detailsClass);
                })
            }
            if (form.extensions) {
                form.extensions.forEach(extension => {
                    extension.formConfig = this.detectFormConfig(extension.extensionClass);
                })
            }
        });

        setDynamicPermissions(this.forms);
        const {lists, editors} = getCustomizations();
        this.setCustomEditors(editors);
        this.setCustomLists(lists);
        this.permissions = null;
        this.resetPermissions();
    }

    async reload() {
        await this._reloadAndSetup();
        EventBus.$emit(Events.menuUpdated, this.forms);
        return this.forms;
    }

    async load(force = false) {
        if (this.forms && !force) {
            return this.forms;
        }
        await this._reloadAndSetup();

        return this.forms;
    }

    viewByTypeAndName(type, viewName) {
        let formConfig = this.detectFormConfig(type);
        let form = this.forms.find(f => f.formClassLong === formConfig.formClassLong && f.viewName === viewName);
        return form;
    }

    formByType(type) {
        if (type instanceof DynamicFormConfig) {
            return type;
        }
        // console.log(JSON.stringify(type));
        if (!_.isString(type)) {
            if (type?.__typename__) {
                type = type.__typename__;
            } else {
                type = type?.prototype?.__metadata__?.__type__ ?? type?.__metadata__?.__type__;
            }
        }
        if (!type) {
            throw new Error("Type not recognized:" + JSON.stringify(type));
        }


        let form = this.forms.find(f => f.formClassLong === type || f.formCode == type);
        if (!form) {
            form = this.forms.find(f => f.formClassLong.endsWith("." + type));
        }
        return form;
    }

    async runFormActivityList(formId, props) {
        return await this.runFormActivityListFromConfig(this.formsById.get(formId), props);
    }

    async replaceFormActivityList(formId, props) {
        return await this.runFormActivityListFromConfig(this.formsById.get(formId), props, false, true);
    }

    /**
     * @param {FormConfig} formConfig
     * @param {*?} props
     * @param {boolean} raw
     * @return {Promise<Activity>}
     */
    async runFormActivityListFromConfig(formConfig, props = {}, raw = false, replaceActivity = false) {
        if (!props) {
            props = {};
        }
        if (!raw) {
            if (this.customLists.has(formConfig.formId)) {
                return await ActivityManager.showActivity(this.customLists.get(formConfig.formId), {
                    ...props,
                    formId: formConfig.formId,
                    formConfig
                }, this.getFormTraceLabel(formConfig), replaceActivity);
            }
        }
        return await ActivityManager.showActivity(ActivityList, {
            ...props,
            formId: formConfig.formId,
            formConfig
        }, this.getFormTraceLabel(formConfig), replaceActivity);
    }

    /**
     * @param {FormConfig} formConfig
     * @param {*} props
     * @param {boolean} raw
     * @return {Promise<Activity>}
     */
    async runFormActivityEditFromConfig(formConfig, props = {}, raw = false) {
        if (!props) {
            props = {};
        }

        let formId;
        let id = props.entityId || props.formEntity?.id;
        if (id) {
            formId = `${formConfig.formId}::${id}`;
        }

        let found = false;
        ActivityManager.forEachActivity((activity => {
            if (activity.__formId && activity.__formId.has(formId)) {
                found = true;
                activity.blinkOnBar();
            }
        }));

        if (found) {
            // DialogManager.info($tr("cantOpenRecord", "Can't open record"),
            //     $tr('formsLoader.already.record.open', 'Record is already open on the stack')).then();

            return null;
        }

        if (!raw) {
            if (this.customEditors.has(formConfig.formId)) {
                let act = await ActivityManager.showActivity(this.customEditors.get(formConfig.formId), {
                    ...props,
                    entityId: props.entityId || props.formEntity?.id,
                    formId: formConfig.formId,
                    formConfig
                }, this.getFormTraceLabel(formConfig));
                if (formId) {
                    if (!act.__formId) {
                        act.__formId = new Set();
                    }
                    act.__formId.add(formId);
                }
                return act;
            }
        }
        let act = await ActivityManager.showActivity(ActivityEdit, {
            ...props,
            entityId: props.entityId || props.formEntity?.id,
            formId: formConfig.formId,
            formConfig
        }, this.getFormTraceLabel(formConfig));
        if (formId) {
            if (!act.__formId) {
                act.__formId = new Set();
            }
            act.__formId.add(formId);
        }
        return act;
    }

    formInEnabledModules(typeOrTypenameOrFormConfigOrFormId) {
        let form = FormsLoader.detectFormConfig(typeOrTypenameOrFormConfigOrFormId);
        if (!form.modules) {
            return true;
        }
        return SessionManager.anyModuleEnabled(form.modules);
    }

    /**
     * @param {FormConfig|String|Type|number} typeOrTypenameOrFormConfigOrFormId
     * @param {boolean} ignoreNotFound
     * @return {FormConfig}
     */
    detectFormConfig(typeOrTypenameOrFormConfigOrFormId, ignoreNotFound = false, realConfFromView = false) {
        if (typeOrTypenameOrFormConfigOrFormId instanceof FormConfig) {
            if (realConfFromView) {
                if (typeOrTypenameOrFormConfigOrFormId.isView) {
                    typeOrTypenameOrFormConfigOrFormId = this.detectFormConfig(typeOrTypenameOrFormConfigOrFormId.masterClass, ignoreNotFound);
                }
            }
            return typeOrTypenameOrFormConfigOrFormId;
        }
        if (this.formsById.has(typeOrTypenameOrFormConfigOrFormId)) {
            let formConfig = this.formsById.get(typeOrTypenameOrFormConfigOrFormId);
            if (realConfFromView) {
                if (formConfig.isView) {
                    formConfig = this.detectFormConfig(formConfig.masterClass, ignoreNotFound);
                }
            }
            return formConfig;
        }
        let formConfig = this.formByType(typeOrTypenameOrFormConfigOrFormId);
        if (!formConfig) {
            if (ignoreNotFound) {
                return undefined;
            }
            Log.fatal("Unrecognized form config" + JSON.stringify(typeOrTypenameOrFormConfigOrFormId), true);
            throw Error("Form not found");
        }
        if (realConfFromView) {
            if (formConfig.isView) {
                formConfig = this.detectFormConfig(formConfig.masterClass, ignoreNotFound);
            }
        }
        return formConfig;
    }

    /**
     * @param {FormConfig|String|Type|number} typeOrTypenameOrFormConfigOrFormId
     * @param {Object} props
     * @return {Promise<null|Activity>}
     */
    async showForm(typeOrTypenameOrFormConfigOrFormId, props) {
        if (!props) {
            props = {};
        }
        return this.runFormActivityEditFromConfig(this.detectFormConfig(typeOrTypenameOrFormConfigOrFormId, false, true), props);
    }

    /**
     * @param {FormConfig|String|Type|number} typeOrTypenameOrFormConfigOrFormId
     * @param {Object} props
     * @return {Promise<null|Activity>}
     */
    async showList(typeOrTypenameOrFormConfigOrFormId, props) {
        if (!props) {
            props = {};
        }
        return this.runFormActivityList(this.detectFormConfig(typeOrTypenameOrFormConfigOrFormId).formId, props);
    }

    /**
     * @param {FormConfig|String|Type|number} typeOrTypenameOrFormConfigOrFormId
     * @param {Object} props
     * @param excludedIds
     * @param activityType
     * @return {Promise<null|Activity>}
     */
    async showSelectList(typeOrTypenameOrFormConfigOrFormId, props, excludedIds = [], activityType = "") {
        if (!props) {
            props = {};
        }
        let context = props?.context ?? new FormsListContext();
        context.mode = 'select';
        context.excludedIds = excludedIds;
        context.onlyIds = props.onlyIds;
        context.activityType = activityType;
        context.multiSelect = props.multiSelect ?? false;
        context.showDelete = props.showDelete;
        context.showAdd = props.showAdd;
        return this.runFormActivityList(this.detectFormConfig(typeOrTypenameOrFormConfigOrFormId).formId, {
            ...props,
            context
        });
    }

    /**
     * @param {FormConfig|String|Type|number} typeOrTypenameOrFormConfigOrFormId
     * @param formValidatedFieldWithParent
     * @param {Object} props
     * @param excludedIds
     * @param activityType
     * @return {Promise<null|Activity>}
     */
    async showSelectListWithParentId(typeOrTypenameOrFormConfigOrFormId, formValidatedFieldWithParent, props, excludedIds = [], activityType = "") {
        if (!props) {
            props = {};
        }
        let context = props?.context ?? {};
        context.mode = 'select';
        context.parentFk = props?.parentFk;
        context.excludedIds = excludedIds;
        context.activityType = activityType;
        context.formValidatedFieldWithParent = formValidatedFieldWithParent;
        context.showDelete = props.showDelete;
        context.showAdd = props.showAdd;
        return this.runFormActivityList(this.detectFormConfig(typeOrTypenameOrFormConfigOrFormId).formId,
            {...props, context});
    }

    /**
     * @param {FormConfig|String|Type|number} typeOrTypenameOrFormConfigOrFormId
     * @return {Object|null}
     */
    formPermission(typeOrTypenameOrFormConfigOrFormId) {
        let formConfig = this.detectFormConfig(typeOrTypenameOrFormConfigOrFormId);
        return formConfig.restrictedList ? Perms[formConfig.effectivePermissionList] : null;
    }

    makePrototype(form) {
        let proto = new DynamicEntity();

        let metadata = {};
        metadata.__dynamic__ = true;
        metadata.__type__ = form.formClassLong;
        metadata.__type_short__ = form.formCode;
        proto.__metadata__ = metadata;

        return proto;
    }

    makeViewRowInstance(typeName, prop, elementMeta) {
        let newProp = new ViewBase();
        newProp.__metadata__ = elementMeta;

        let propKeys = Object.keys(prop);

        for (let j = 0; j < propKeys.length; j++) {
            let pk = propKeys[j];
            if (!pk.startsWith("__")) {
                let rawValue = prop[pk];

                if (!elementMeta.hasOwnProperty(pk)) {
                    Log.warn("missing metadata for: " + pk + " in " + typeName);
                }
                if (!elementMeta[pk]) {
                    Log.warn("missing metadata for: " + pk + " in " + typeName);
                }
                if (!elementMeta[pk] || !elementMeta[pk].remoteType) {
                    Log.warn("No metadata");
                }
                let propType = elementMeta[pk].remoteType;
                newProp[pk] = RpcClass.makeFieldInstance(rawValue, propType);
            } else {
                newProp[pk] = prop[pk];
            }
        }
        return newProp;
    }

    makeInstance(typeName, prop, elementMeta) {
        if (!typeName.startsWith("dyn.")) {
            return this.makeViewRowInstance(typeName, prop, elementMeta);
        }

        let proto = this.prototypes.get(typeName);
        let newProp = Object.create(proto);
        let propKeys = Object.keys(prop);

        for (let j = 0; j < propKeys.length; j++) {
            let pk = propKeys[j];
            if (!pk.startsWith("__")) {
                let rawValue = prop[pk];
                // if(!proto.__metadata__.hasOwnProperty(pk)){
                //     Log.warn("missing metadata for: " + pk + " in " + typeName);
                // }
                // if(!proto.__metadata__[pk]){
                //     Log.warn("missing metadata for: " + pk + " in " + typeName);
                // }
                //let propType = proto.__metadata__[pk].remoteType;
                let propType = null;
                newProp[pk] = RpcClass.makeFieldInstance(rawValue, propType);
            } else {
                newProp[pk] = prop[pk];
            }
        }

        return newProp;
    }
}

const FormsLoader = new _Loader();
export default FormsLoader;
window['FormsLoader'] = FormsLoader;